import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';

const SignaturePad = React.forwardRef(({ onChange, disabled }, ref) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const hasSignatureRef = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);

  const prepareCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const width = Math.round(rect.width * ratio);
    const height = Math.round(rect.height * ratio);
    if (canvas.width === width && canvas.height === height) return;
    const snapshot = hasSignatureRef.current && canvas.width && canvas.height
      ? canvas.toDataURL()
      : null;
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    context.scale(ratio, ratio);
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.lineWidth = 2;
    context.strokeStyle = '#212529';
    if (snapshot) {
      const image = new Image();
      image.onload = () => context.drawImage(image, 0, 0, rect.width, rect.height);
      image.src = snapshot;
    }
  };

  useEffect(() => {
    prepareCanvas();
    const resizeObserver = new ResizeObserver(prepareCanvas);
    if (canvasRef.current) resizeObserver.observe(canvasRef.current);
    window.addEventListener('resize', prepareCanvas);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', prepareCanvas);
    };
  }, []);

  const point = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const start = (event) => {
    if (disabled) return;
    event.preventDefault();
    drawingRef.current = true;
    canvasRef.current.setPointerCapture(event.pointerId);
    const context = canvasRef.current.getContext('2d');
    const position = point(event);
    context.beginPath();
    context.moveTo(position.x, position.y);
  };

  const draw = (event) => {
    if (!drawingRef.current || disabled) return;
    event.preventDefault();
    const position = point(event);
    const context = canvasRef.current.getContext('2d');
    context.lineTo(position.x, position.y);
    context.stroke();
    if (!hasSignatureRef.current) {
      hasSignatureRef.current = true;
      setHasSignature(true);
      onChange(true);
    }
  };

  const stop = (event) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (canvasRef.current.hasPointerCapture(event.pointerId)) {
      canvasRef.current.releasePointerCapture(event.pointerId);
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    hasSignatureRef.current = false;
    setHasSignature(false);
    onChange(false);
  };

  const toFile = () => new Promise((resolve, reject) => {
    canvasRef.current.toBlob((blob) => {
      if (!blob) {
        reject(new Error('The signature image could not be created.'));
        return;
      }
      resolve(new File([blob], `member-signature-${Date.now()}.png`, { type: 'image/png' }));
    }, 'image/png');
  });

  React.useImperativeHandle(ref, () => ({ toFile }));

  return (
    <div>
      <canvas
        ref={canvasRef}
        aria-label="Signature drawing area"
        className="w-100 bg-white border rounded"
        style={{ height: '220px', cursor: disabled ? 'not-allowed' : 'crosshair', touchAction: 'none' }}
        onPointerDown={start}
        onPointerMove={draw}
        onPointerUp={stop}
        onPointerCancel={stop}
      />
      <div className="d-flex justify-content-between align-items-center mt-2">
        <small className="text-muted">Sign using a finger, stylus, touchpad, or mouse.</small>
        <Button type="button" color="secondary" size="sm" outline disabled={!hasSignature || disabled} onClick={clear}>
          Clear signature
        </Button>
      </div>
    </div>
  );
});

SignaturePad.displayName = 'SignaturePad';

SignaturePad.propTypes = {
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

SignaturePad.defaultProps = { disabled: false };

export default SignaturePad;
